// components/maps/MapControls.jsx
import React, { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Layers, Navigation, Maximize2, Minimize2, Crosshair } from 'lucide-react';

// Recenter Control Component (uses Leaflet native control)
export const RecenterControl = ({ position = 'topleft', onClick }) => {
  const map = useMap();
  
  useEffect(() => {
    const control = L.Control.extend({
      options: { position },
      onAdd: function() {
        const button = L.DomUtil.create('button', 'leaflet-control-recenter');
        button.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 2v4m0 12v4M2 12h4m12 0h4"/>
          </svg>
        `;
        button.title = 'Recenter map on your location';
        button.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        };
        L.DomEvent.disableClickPropagation(button);
        return button;
      }
    });
    
    const recenterControl = new control({ position });
    map.addControl(recenterControl);
    
    return () => map.removeControl(recenterControl);
  }, [map, position, onClick]);
  
  return null;
};

// Layer Toggle Control Component
export const LayerToggle = ({ position = 'topright', currentLayer, onToggle }) => {
  const positionClasses = {
    topright: 'top-4 right-4',
    topleft: 'top-4 left-4',
    bottomright: 'bottom-4 right-4',
    bottomleft: 'bottom-4 left-4'
  };

  return (
    <div className={`absolute ${positionClasses[position]} z-[1000]`}>
      <button
        onClick={onToggle}
        className="bg-white p-2.5 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
        title={`Switch to ${currentLayer === 'street' ? 'satellite' : 'street'} view`}
      >
        <Layers size={20} className="text-gray-700" />
      </button>
    </div>
  );
};

// Legend Component
export const MapLegend = ({ position = 'bottomleft', items }) => {
  const defaultItems = items || [
    { color: 'bg-blue-500', label: 'Hospital', icon: null },
    { color: 'bg-green-600', label: '24/7 Emergency', icon: '!' },
    { color: 'bg-red-600', label: 'Selected', icon: null }
  ];

  const positionClasses = {
    topright: 'top-4 right-4',
    topleft: 'top-4 left-4',
    bottomright: 'bottom-4 right-4',
    bottomleft: 'bottom-4 left-4'
  };

  return (
    <div className={`absolute ${positionClasses[position]} bg-white rounded-lg shadow-md p-3 z-[1000]`}>
      <h4 className="text-xs font-semibold text-gray-700 mb-2">Legend</h4>
      <div className="space-y-1.5">
        {defaultItems.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className={`w-4 h-4 ${item.color} rounded-full flex items-center justify-center`}>
              {item.icon && (
                <span className="text-white text-[10px] font-bold">{item.icon}</span>
              )}
            </div>
            <span className="text-xs text-gray-600">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Current Location Button Component
export const CurrentLocationButton = ({ position = 'bottomright', onClick, loading = false }) => {
  const positionClasses = {
    topright: 'top-4 right-4',
    topleft: 'top-4 left-4',
    bottomright: 'bottom-4 right-4',
    bottomleft: 'bottom-4 left-4'
  };

  return (
    <div className={`absolute ${positionClasses[position]} z-[1000]`}>
      <button
        onClick={onClick}
        disabled={loading}
        className="bg-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Get current location"
      >
        {loading ? (
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Crosshair size={20} className="text-blue-600" />
        )}
      </button>
    </div>
  );
};

// Fullscreen Toggle Component
export const FullscreenToggle = ({ position = 'topright', isFullscreen, onToggle }) => {
  const positionClasses = {
    topright: 'top-16 right-4',
    topleft: 'top-16 left-4',
    bottomright: 'bottom-16 right-4',
    bottomleft: 'bottom-16 left-4'
  };

  return (
    <div className={`absolute ${positionClasses[position]} z-[1000]`}>
      <button
        onClick={onToggle}
        className="bg-white p-2.5 rounded-lg shadow-md hover:shadow-lg transition-all hover:scale-105"
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? (
          <Minimize2 size={20} className="text-gray-700" />
        ) : (
          <Maximize2 size={20} className="text-gray-700" />
        )}
      </button>
    </div>
  );
};

// Combined Map Controls Styles
export const MapControlsStyles = () => (
  <style>{`
    .leaflet-control-recenter {
      width: 36px;
      height: 36px;
      background: white;
      border: 2px solid rgba(0,0,0,0.2);
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #374151;
      transition: all 0.2s;
    }
    
    .leaflet-control-recenter:hover {
      background: #f3f4f6;
      transform: scale(1.05);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .leaflet-control-recenter:active {
      transform: scale(0.95);
    }
    
    .leaflet-control-recenter:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .leaflet-control-recenter svg {
      display: block;
    }
  `}</style>
);

// Main MapControls Component
const MapControls = ({
  userLocation,
  onRecenter,
  currentLayer,
  onLayerToggle,
  legendItems,
  showRecenter = true,
  showLayerToggle = true,
  showLegend = true,
  showCurrentLocation = false,
  onCurrentLocation,
  loadingLocation = false,
  showFullscreen = false,
  isFullscreen = false,
  onFullscreenToggle,
  showZoomLevel = false
}) => {
  return (
    <>
      {showRecenter && userLocation && (
        <RecenterControl position="topleft" onClick={onRecenter} />
      )}
      {showLayerToggle && (
        <LayerToggle 
          position="topright" 
          currentLayer={currentLayer} 
          onToggle={onLayerToggle} 
        />
      )}
      {showLegend && (
        <MapLegend position="bottomleft" items={legendItems} />
      )}
      {showCurrentLocation && (
        <CurrentLocationButton 
          position="bottomright" 
          onClick={onCurrentLocation}
          loading={loadingLocation}
        />
      )}
      {showFullscreen && (
        <FullscreenToggle 
          position="topright" 
          isFullscreen={isFullscreen}
          onToggle={onFullscreenToggle}
        />
      )}
      <MapControlsStyles />
    </>
  );
};

export default MapControls;